/**
 * Error Boundary auf Seiten-Ebene (frontend-ux.md): niemals eine weisse Seite,
 * immer eine Fallback-UI mit „Seite neu laden".
 *
 * React bietet dafuer bis heute nur Klassenkomponenten an.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

type Props = WithTranslation & { children: ReactNode };
type State = { hasError: boolean };

class ErrorBoundaryBase extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Solange es kein Error-Tracking gibt, bleibt die Browser-Konsole die
    // einzige Spur — besser als ein still verschluckter Fehler.
    console.error('render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { t } = this.props;
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-xl font-semibold">{t('error.boundary_title')}</h1>
          <p className="text-sm text-muted-foreground">{t('error.boundary_description')}</p>
          <Button onClick={() => window.location.reload()}>{t('common.reload')}</Button>
        </div>
      </div>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryBase);
